/**
 * Soft≠Live Page157 — SA Factory residual liquid (page77/129 buses + status).
 * Mount: /sa/factory
 * Buses:
 *   GET /factory/status (always-on Live posture)
 *   GET /factory/devices · GET /factory/ping
 *   POST /factory/devices · POST /factory/devices/{serial}/block
 * Soft residual deleted:
 *  - sandpit desk without liquid rail / KPI / Re-read / status SoT
 *  - bare Ping / refresh / Block CTAs (must be labeled)
 *  - Soft invent n written when write fails
 *  - Soft success when seat gated (honest blank)
 */
import { useMemo, useState } from "react";
import { html, F, Ico, Input, Field, Card, Alert, Ticker, KV, go } from "./ui.js";
import { api, path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";
import { Unavailable } from "./sa-shared.js";

/** Pure: factory list/block seat — Live grant only (client mirror of status). */
export function canManageFactoryOf(me) {
  if (!me) return false;
  const r = String(me.role || "");
  if (r === "superadmin") return true;
  const caps = me.caps || me.sa_capabilities || [];
  const list = Array.isArray(caps) ? caps : String(caps || "").split(",");
  return list.some((c) =>
    /sa_device_admin|sa_full_access|sa_full/i.test(String(c || "").trim()),
  );
}

/** Pure: row normalize — never invent Soft serial/status. */
export function factoryDeviceOf(row) {
  if (!row || typeof row !== "object") return null;
  const serial = String(row.serial || "").trim();
  if (!serial) return null;
  return {
    serial,
    status: String(row.status || "").trim() || "—",
    batch: String(row.batch || "").trim() || "—",
    mac: String(row.mac_nocolon || row.mac || "").trim() || "—",
  };
}

/** Pure: Register items. */
export function factoryRegisterOf(pack) {
  const items = (pack && (pack.items || pack.devices)) || [];
  if (!Array.isArray(items)) return [];
  return items.map(factoryDeviceOf).filter(Boolean);
}

/** Pure: KPI strip from Register only. */
export function factoryKpisOf(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const blocked = list.filter((r) => r && r.status === "blocked").length;
  const claimed = list.filter((r) => r && r.status === "claimed").length;
  const batches = new Set(list.map((r) => r && r.batch).filter((b) => b && b !== "—"));
  return {
    serials: list.length,
    blocked,
    claimed,
    batches: batches.size,
  };
}

export function SaFactoryDesk({ toast }) {
  const { me } = useSession();
  const posture = useApi(path("get__factory_status"));
  // Fail-closed while loading — Soft invent factory list from client role deleted.
  const post = posture.loading || !posture.data ? {} : (posture.data || {});
  const seat = post.list_allowed === true;
  const detail = post.detail || null;
  const list = useApi(seat ? path("get__factory_devices") : "");
  const { run, busy, error, envelope } = useAction();
  const [factoryToken, setFactoryToken] = useState("");
  const [blockSerial, setBlockSerial] = useState("");
  const [batch, setBatch] = useState("");
  const [itemsJson, setItemsJson] = useState("[]");
  const [pingFace, setPingFace] = useState("");

  const rows = useMemo(() => factoryRegisterOf(list.data), [list.data]);
  const kpis = useMemo(() => factoryKpisOf(rows), [rows]);
  const denied = !!(list.error && /\b40[13]\b|forbidden|insufficient|capability/i.test(String(list.error)));

  const tokenHeaders = () => {
    const t = factoryToken.trim();
    return t ? { "X-Factory-Token": t } : {};
  };

  const reRead = () => {
    posture.reload && posture.reload();
    list.reload && list.reload();
    toast && toast("Factory register + posture re-read", "go");
  };

  const ping = async () => {
    setPingFace("");
    try {
      const out = await api(path("get__factory_ping"), { headers: tokenHeaders() });
      setPingFace(JSON.stringify(out, null, 2));
      toast && toast("Factory ping", "go");
    } catch (e) {
      setPingFace("");
      toast && toast(String((e && e.message) || e || "Ping refused — need superadmin Bearer or X-Factory-Token"), "bad");
    }
  };

  const block = async () => {
    const s = blockSerial.trim();
    if (!s) { toast && toast("Serial required", "warn"); return; }
    const out = await run(path("post__factory_devices_p_block", { pathParams: [s] }), { body: {} });
    if (!out) {
      toast && toast(error || "Block refused — nothing was changed", "bad");
      return;
    }
    toast && toast("Serial blocked · " + s, "warn");
    list.reload && list.reload();
  };

  const writeBatch = async () => {
    let parsed;
    try { parsed = JSON.parse(itemsJson); } catch (_) {
      toast && toast("Items must be valid JSON array", "bad"); return;
    }
    if (!Array.isArray(parsed) || !parsed.length) {
      toast && toast("Provide at least one factory item", "warn"); return;
    }
    if (batch.trim()) {
      parsed = parsed.map((row) => (row && typeof row === "object"
        ? Object.assign({}, row, { batch: row.batch || batch.trim() })
        : row));
    }
    try {
      const out = await api(path("post__factory_devices"), {
        method: "POST",
        body: { items: parsed },
        headers: tokenHeaders(),
        timeoutMs: 120000,
      });
      const n = out && (out.written ?? out.ok_count ?? (Array.isArray(out.accepted) ? out.accepted.length : null));
      toast && toast(n != null ? `Factory write · ${n} accepted` : "Factory write", "go");
      list.reload && list.reload();
    } catch (e) {
      toast && toast(String((e && e.message) || e || "Factory write refused — cookie session alone is not enough; paste X-Factory-Token or use Bearer superadmin JWT"), "bad");
    }
  };

  if (!seat) {
    return html`<div className="sa-factory-desk sa-factory-desk--liquid"
      data-cmc-page="page129-sa-factory" data-cmc-legacy="page77-sa-factory"
      data-cmc-residual="page157-factory">
      <div className="sa-factory-desk__rail">
        <div>
          <p className="eyebrow">Platform · Factory</p>
          <h2 className="sa-factory-desk__title">Seat closed</h2>
          <p className="note">Factory register stays empty until this seat opens.</p>
        </div>
        <div className="btn-row sa-factory-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}>
            Re-read posture</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/sa/custody")}>Custody</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/sa/audit")}>Audit</button>
        </div>
      </div>
      <${Card} title="Factory posture" meta="Live">
        ${posture.loading ? html`<p className="note">Reading posture…</p>` : null}
        ${posture.error ? html`<${Alert} tone="warn" title="Posture refused"
          say=${String(posture.error)}/>` : null}
        ${!posture.loading && !posture.error ? html`
          <p className="note">
            list_allowed no · detail ${detail || "factory_seat_gated"}
            · enforce_registration ${post.enforce_registration ? "yes" : "no"}
            · token_configured ${post.token_configured ? "yes" : "no"}
          </p>` : null}
      <//>
      <${Alert} tone="info" title="Factory administration not on this account"
        say=${"List / block need superadmin or sa_device_admin / sa_full_access. This account is "
          + (me && me.role ? me.role : "operator")
          + " — brochure register invent is not offered."}/>
      <${Card} title="Factory register" meta="gated">
        <p className="note">No factory rows are offered on this grant.</p>
      <//>
    </div>`;
  }

  return html`<div className="sa-factory-desk sa-factory-desk--liquid"
    data-cmc-page="page129-sa-factory" data-cmc-legacy="page77-sa-factory"
    data-cmc-residual="page157-factory">
    <div className="sa-factory-desk__rail">
      <div>
        <p className="eyebrow">Platform · Factory</p>
        <h2 className="sa-factory-desk__title">Serials</h2>
        <p className="note">
          High-speed factory buses. Empty register means empty — not a brochure invent.
          Bulk write answers only from the server. Posture only.
        </p>
      </div>
      <div className="btn-row sa-factory-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
          disabled=${list.loading || posture.loading}>
          <${Ico} d="refresh" w=${14}/> Re-read
        </button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/sa/custody")}>Custody</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/sa/tenant")}>Tenants</button>
      </div>
    </div>

    <${Card} title="Factory posture" meta="Live">
      ${posture.loading ? html`<p className="note">Reading posture…</p>` : null}
      ${posture.error ? html`<${Alert} tone="warn" title="Posture refused"
        say=${String(posture.error)}/>` : null}
      ${!posture.loading && !posture.error ? html`
        <div className="sa-factory-desk__kpis" role="group" aria-label="Factory posture">
          ${[
            ["List seat", seat ? "open" : "gated", seat ? "1" : "0", "status Live"],
            ["Enforce reg", post.enforce_registration ? "on" : "off", post.enforce_registration ? "1" : "0", "ENV Live"],
            ["Token plane", post.token_configured ? "set" : "empty", post.token_configured ? "1" : "0", "FACTORY_API_TOKEN"],
            ["QR verify", post.qr_verify_on_register ? "on" : "off", post.qr_verify_on_register ? "1" : "0", "register Live"],
          ].map(([k, v, on, em]) => html`
            <div key=${k} className="sa-factory-desk__kpi" data-on=${on}>
              <span>${k}</span>
              <b>${v}</b>
              <em>${em}</em>
            </div>`)}
        </div>
        <p className="note">
          List and write only after seats open
          · write ${post.write_allowed === true ? "open" : "sealed"}
        </p>` : null}
    <//>

    <${Alert} tone="info" title="Two auth planes"
      say="List and block use your signed-in platform session. Ping and bulk register need full factory credentials. Write counts only appear after the server accepts them."/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    ${list.error ? html`
      <${Alert} tone=${denied ? "warn" : "bad"} title=${denied ? "Register gated" : "Register read failed"}
        say=${String(list.error)}/>` : null}

    <div className="sa-factory-desk__kpis" role="group" aria-label="Factory KPIs">
      ${[
        ["Serials", kpis.serials, kpis.serials ? "1" : "0", "Register"],
        ["Claimed", kpis.claimed, kpis.claimed ? "1" : "0", "status Live"],
        ["Blocked", kpis.blocked, kpis.blocked ? "1" : "0", "RMA Live"],
        ["Batches", kpis.batches, kpis.batches ? "1" : "0", "distinct Live"],
      ].map(([k, v, on, em]) => html`
        <div key=${k} className="sa-factory-desk__kpi" data-on=${on}>
          <span>${k}</span>
          <b><${Ticker} value=${v}/></b>
          <em>${em}</em>
        </div>`)}
    </div>

    <${Card} title="Environment probe" meta="Live">
      <${Field} label="X-Factory-Token (optional for ping/write)"
          hint="Held only in this tab. Never logged. Leave empty to try Bearer-only clients.">
        <${Input} icon="key" type="password" value=${factoryToken}
          onChange=${(e) => setFactoryToken(e.target.value)} autocomplete="off"/>
      <//>
      <div className="btn-row sa-factory-desk__acts">
        <button className="btn btn--ghost btn--sm" type="button" onClick=${ping}>
          Ping factory</button>
      </div>
      ${pingFace ? html`<pre className="m sa-factory-desk__pre">${pingFace}</pre>` : null}
    <//>

    <${Card} title="Factory register" meta="Live"
        sub="Rows for superadmin / sa_device_admin. Empty means empty — not a brochure invent.">
      ${list.error ? html`<${Unavailable} r=${list} what="the factory register"/>` : null}
      ${!list.loading && !list.error && !rows.length ? html`<p className="note">Empty — no factory rows returned.</p>` : null}
      ${rows.length ? html`
        <div className="sa-factory-desk__table-wrap">
          <table className="tbl">
            <thead><tr><th>Serial</th><th>Status</th><th>Batch</th><th>MAC</th></tr></thead>
            <tbody>${rows.slice(0, 60).map((row) => html`
              <tr key=${row.serial} onClick=${() => setBlockSerial(row.serial)} style=${{ cursor: "pointer" }}>
                <td><b>${row.serial}</b></td>
                <td><span className="chip" data-tone=${row.status === "blocked" ? "bad" : row.status === "claimed" ? "" : "warn"}>${row.status}</span></td>
                <td><span className="m">${row.batch}</span></td>
                <td><span className="m">${row.mac}</span></td>
              </tr>`)}</tbody>
          </table>
        </div>
        <p className="note">Click a row to fill Block serial. Soft auto-block is forbidden.</p>` : null}
    <//>

    <div className="grid g2 sa-factory-desk__grid">
      <${Card} title="Block a serial" meta="Block"
          sub="RMA path. Session principal must hold superadmin or sa_device_admin.">
        <${Field} label="Serial"><${Input} icon="chip" value=${blockSerial}
          onChange=${(e) => setBlockSerial(e.target.value)} placeholder="SN-…"/><//>
        ${error ? html`<${Alert} tone="warn" title="Last block failed" say=${error}/>` : null}
        <button className="btn btn--danger btn--sm" disabled=${busy || !blockSerial.trim()} onClick=${block}>
          Block serial</button>
      <//>
      <${Card} title="Bulk register" meta="Bulk register"
          sub="Paste the manufacturing JSON. Soft “n written” toasts were removed — only the server answer counts.">
        <${Field} label="Batch tag (optional overlay)">
          <${Input} icon="chip" value=${batch} onChange=${(e) => setBatch(e.target.value)}/>
        <//>
        <${Field} label="Items JSON" hint='[{"serial":"SN-…","mac_nocolon":"aabbccddeeff","qr_code":"…"}]'>
          <textarea className="inp" rows="6" value=${itemsJson}
            onChange=${(e) => setItemsJson(e.target.value)} style=${{ fontFamily: "var(--mono)", width: "100%" }}/>
        <//>
        <button className="btn btn--go btn--sm" onClick=${writeBatch}>Write batch</button>
        <p className="note">One bad row does not fail the batch — the server lists rejects. A malformed call writes nothing.</p>
      <//>
    </div>
  </div>`;
}

export function Factory({ toast }) {
  return html`<${F} data-cmc-page="page77-sa-factory-mount">
    <${SaFactoryDesk} toast=${toast}/>
  <//>`;
}
